/**
 * Deck management utilities for Durak
 * Handles deck creation, shuffling, and card dealing
 */

import type { Card, Suit, Rank, Difficulty } from '../types/game';
import { SUITS, RANKS, RANK_VALUES, RANK_DIFFICULTY } from '../types/game';

/**
 * Creates a standard 36-card Durak deck
 * Cards are ordered by suit, then rank (6-A)
 */
export function buildDeck(): Card[] {
  const deck: Card[] = [];

  for (const suit of SUITS) {
    for (const rank of RANKS) {
      deck.push({
        suit,
        rank,
        value: RANK_VALUES[rank],
      });
    }
  }

  return deck;
}

/**
 * Fisher-Yates shuffle algorithm
 * Creates a new shuffled array without mutating input
 */
export function shuffle<T>(array: readonly T[]): T[] {
  const arr = [...array];

  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }

  return arr;
}

/**
 * Shuffles a deck in place (mutates original)
 * Use when performance matters and mutation is acceptable
 */
export function shuffleInPlace<T>(array: T[]): T[] {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

/**
 * Deals cards from the deck to hands
 * Mutates the deck array (removes dealt cards)
 */
export function dealCards(
  deck: Card[],
  count: number
): { dealt: Card[]; remaining: Card[] } {
  const dealt = deck.splice(0, count);
  return { dealt, remaining: deck };
}

/**
 * Determines the trump suit from the bottom card of the deck
 * Per Durak rules, the last card in the deck reveals the trump suit
 */
export function determineTrump(deck: readonly Card[]): {
  trump: Suit;
  trumpCard: Card;
} {
  const trumpCard = deck[deck.length - 1];
  return {
    trump: trumpCard.suit,
    trumpCard,
  };
}

/**
 * Gets the difficulty tier for a card rank
 */
export function getCardDifficulty(rank: Rank): Difficulty {
  return RANK_DIFFICULTY[rank];
}

/**
 * Checks if a card is a trump card
 */
export function isTrump(card: Card, trumpSuit: Suit): boolean {
  return card.suit === trumpSuit;
}

/**
 * Checks if a suit is red (hearts or diamonds)
 */
export function isRedSuit(suit: Suit): boolean {
  return suit === '♥' || suit === '♦';
}

/**
 * Formats a card for display
 */
export function formatCard(card: Card): string {
  return `${card.rank}${card.suit}`;
}

/**
 * Creates a card comparator for sorting
 * Sorts by suit (non-trump first), then by rank ascending
 */
export function createCardComparator(trumpSuit: Suit): (a: Card, b: Card) => number {
  return (a: Card, b: Card): number => {
    const aIsTrump = a.suit === trumpSuit;
    const bIsTrump = b.suit === trumpSuit;

    // Trump cards come after non-trump
    if (aIsTrump && !bIsTrump) return 1;
    if (!aIsTrump && bIsTrump) return -1;

    // Same trump status - sort by value
    return a.value - b.value;
  };
}

/**
 * Sorts a hand for optimal display
 * Non-trump cards first (sorted low to high), then trump cards
 */
export function sortHand(hand: Card[], trumpSuit: Suit): Card[] {
  return [...hand].sort(createCardComparator(trumpSuit));
}

/**
 * Finds the lowest card in a hand that can be played
 * Prefers lowest non-trump, falls back to lowest trump
 */
export function findLowestCard(hand: Card[], trumpSuit: Suit): Card | null {
  if (hand.length === 0) return null;

  const sorted = sortHand(hand, trumpSuit);
  return sorted[0];
}

/**
 * Validates that a deck has the correct composition
 */
export function validateDeck(deck: readonly Card[]): boolean {
  if (deck.length !== 36) return false;

  const expectedCards = new Set<string>();
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      expectedCards.add(`${rank}-${suit}`);
    }
  }

  const actualCards = new Set(deck.map(c => `${c.rank}-${c.suit}`));

  if (expectedCards.size !== actualCards.size) return false;

  for (const card of expectedCards) {
    if (!actualCards.has(card)) return false;
  }

  return true;
}

/**
 * Deck statistics for debugging/testing
 */
export function getDeckStats(deck: readonly Card[]): {
  total: number;
  bySuit: Record<Suit, number>;
  byRank: Record<Rank, number>;
} {
  const bySuit: Record<Suit, number> = { '♠': 0, '♥': 0, '♦': 0, '♣': 0 };
  const byRank: Record<Rank, number> = {
    '6': 0, '7': 0, '8': 0, '9': 0, '10': 0,
    'J': 0, 'Q': 0, 'K': 0, 'A': 0
  };

  for (const card of deck) {
    bySuit[card.suit]++;
    byRank[card.rank]++;
  }

  return {
    total: deck.length,
    bySuit,
    byRank,
  };
}
